/**
 * audience-theme.ts — V4 (palette restreinte)
 * ─────────────────────────────────────────────────────────────────
 * Tokens visuels par profil utilisateur.
 *
 * V4 : passage à une palette restreinte Stripe/Linear/Vercel-style :
 *   - `brand` (emerald) : tone principal pour 5 audiences sur 7
 *   - `accent` (lime)   : RÉSERVÉ aux pôles phares (Artisans / Agriculteurs)
 *   - `corporate`        : Entreprises (ton sobre ink-on-paper)
 *
 * Plus de teal/lavender ni de sage colorés — différenciation par
 * intensité de brand et par code mono dans les chips audience.
 *
 * NB : les clés correspondent au champ `tone` de `audiences.ts`,
 * mais on en propose une enveloppe plus riche (gradient, halo, contrast).
 */

import type { AudienceId, AudienceMeta } from '@shared/constants/audiences';

export type AudienceToneKey = AudienceMeta['tone'];

export interface AudienceTheme {
  /** Identifiant audience source */
  id: AudienceId;
  /** Background mesh hero ('warm' | 'cool' | 'dark') */
  meshVariant: 'warm' | 'cool' | 'dark';
  /** Halo principal hero (rgba via tailwind class shorthand) */
  haloPrimary: string;
  /** Halo secondaire hero */
  haloSecondary: string;
  /** Gradient texte du headline accent */
  gradientText: string;
  /** Pill/badge surface */
  badgeSurface: string;
  /** Pill/badge text */
  badgeText: string;
  /** Pill/badge ring */
  badgeRing: string;
  /** Couleur des chips de proof */
  chipBg: string;
  chipIcon: string;
  /** Bouton primaire variant clé (pour Button variant) */
  primaryButtonVariant: 'primary' | 'brand' | 'accent';
  /** Sous-couleur des micro-bullets sous CTA */
  bulletDot: string;
  /** Couleur halo CTA final (sur fond noir) */
  ctaFinalHalo: string;
  /** Style de la card "side hero" (panel preview) */
  sideCardBg: string;
  /** Accent ring carte side */
  sideCardRing: string;
}

// ────────────────────────────────────────────────────────────────────────
// Tone "BRAND" — Particuliers, Pro, Collectivités, Copropriétés (5 audiences)
// Différenciation entre eux : via codes mono dans les chips, pas via couleur
// ────────────────────────────────────────────────────────────────────────
const BRAND_THEME = (id: AudienceId): AudienceTheme => ({
  id,
  meshVariant: 'warm',
  haloPrimary: 'bg-brand-200/30',
  haloSecondary: 'bg-brand-100/30',
  gradientText:
    'bg-gradient-to-r from-brand-700 via-brand-500 to-accent-500 bg-clip-text text-transparent',
  badgeSurface: 'bg-brand-50',
  badgeText: 'text-brand-800',
  badgeRing: 'ring-brand-200/60',
  chipBg: 'bg-brand-50',
  chipIcon: 'text-brand-700',
  primaryButtonVariant: 'primary',
  bulletDot: 'text-brand-600',
  ctaFinalHalo: 'bg-brand-500/30',
  sideCardBg: 'from-brand-100/30 via-paper to-brand-50/40',
  sideCardRing: 'ring-brand-200/50',
});

// ────────────────────────────────────────────────────────────────────────
// Tone "ACCENT" — Artisans, Agriculteurs (2 audiences = pôles phares)
// Différenciation forte par lime/accent (uniquement ici)
// ────────────────────────────────────────────────────────────────────────
const ACCENT_THEME = (id: AudienceId): AudienceTheme => ({
  id,
  meshVariant: 'warm',
  haloPrimary: 'bg-accent-200/40',
  haloSecondary: 'bg-brand-100/30',
  gradientText:
    'bg-gradient-to-r from-accent-700 via-brand-600 to-accent-500 bg-clip-text text-transparent',
  badgeSurface: 'bg-accent-50',
  badgeText: 'text-accent-800',
  badgeRing: 'ring-accent-200/60',
  chipBg: 'bg-accent-50',
  chipIcon: 'text-accent-700',
  primaryButtonVariant: 'accent',
  bulletDot: 'text-accent-700',
  ctaFinalHalo: 'bg-accent-400/30',
  sideCardBg: 'from-accent-100/30 via-paper to-brand-100/20',
  sideCardRing: 'ring-accent-200/60',
});

// ────────────────────────────────────────────────────────────────────────
// Tone "CORPORATE" — Entreprises, Tertiaire, Industrie (3 audiences B2B/ETI)
// Différenciation par sobriété (ink/stone) au lieu d'une teinte spécifique
// ────────────────────────────────────────────────────────────────────────
const CORPORATE_THEME = (id: AudienceId): AudienceTheme => ({
  id,
  meshVariant: 'dark',
  haloPrimary: 'bg-stone-300/30',
  haloSecondary: 'bg-brand-100/20',
  gradientText:
    'bg-gradient-to-r from-ink via-stone-700 to-brand-700 bg-clip-text text-transparent',
  badgeSurface: 'bg-stone-100',
  badgeText: 'text-ink',
  badgeRing: 'ring-stone-200/60',
  chipBg: 'bg-stone-100',
  chipIcon: 'text-stone-700',
  primaryButtonVariant: 'primary',
  bulletDot: 'text-stone-700',
  ctaFinalHalo: 'bg-stone-500/30',
  sideCardBg: 'from-stone-100/40 via-paper to-brand-50/30',
  sideCardRing: 'ring-stone-200/60',
});

export const AUDIENCE_THEME: Record<AudienceId, AudienceTheme> = {
  particuliers: BRAND_THEME('particuliers'),
  professionnels: BRAND_THEME('professionnels'),
  entreprises: CORPORATE_THEME('entreprises'),
  tertiaire: CORPORATE_THEME('tertiaire'),
  industrie: CORPORATE_THEME('industrie'),
  collectivites: BRAND_THEME('collectivites'),
  coproprietes: BRAND_THEME('coproprietes'),
  artisans: ACCENT_THEME('artisans'),
  agriculteurs: ACCENT_THEME('agriculteurs'),
};

export function getAudienceTheme(id: AudienceId): AudienceTheme {
  return AUDIENCE_THEME[id];
}

/**
 * Mappe un AudienceId vers le tone existant utilisé par les blocs legacy
 * (`JourneyTimeline`, `ToolsForAudience`, `ConcreteCase`).
 *
 * V4 : retour limité à 'brand' | 'accent' | 'sage' (sage = alias stone foncé).
 * Garantit la cohérence avec les composants existants sans les casser.
 */
export function audienceLegacyTone(
  id: AudienceId,
): AudienceMeta['tone'] {
  switch (id) {
    case 'particuliers':
    case 'coproprietes':
    case 'professionnels':
    case 'collectivites':
      return 'brand';
    case 'entreprises':
    case 'tertiaire':
    case 'industrie':
      return 'sage';
    case 'artisans':
    case 'agriculteurs':
      return 'accent';
  }
}
